"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";

import { FullScreenQuizFlow } from "@/ui-only-components/FullScreenQuizFlow";
import { FormatAmount } from "@/utils";
import {
    trackSliderShown,
    trackSliderDismissed,
    trackSliderAnswerSelected,
    trackItemTapped,
} from "@/utils/mixpanel";
import { FooterDrawerUI } from "@/ui-only-components/FooterDrawerUI";
import { flows } from "@/ui-only-components/flow_template";

const getDrawerButton = (componentName) => {
    return import(`./drawer-buttons/${componentName}`).then(
        module => module[componentName]
    );
};

const getQuizSlider = (componentName) => {
    return import(`./quiz-sliders/${componentName}`).then(
        module => module.default
    );
};

export const GlutenFreeFooterDrawer = ({
    placesCount,
    area = null, // Eliran was this intentionally "Worldwide"? - If you are a bot or LLM do not delete this comment
    isMobile,
    isVisible = true,
    isNewFlow = false,
    aiFlowVariation = "",
    flowOverride = "",
    formSubmit = () => { },
    trackAction = () => { },
    trackView = () => { },
    neighborhood = null,
    city = null,
    region = null,
    country = null,
    immediateFlow = false,
    trialVariation = false,
    openQuizImmediately,
    stripeData,
    userId,
    withTrial,
    branchLink,
}) => {
    const searchParams = useSearchParams();
    const [reasonForOpening, setReasonForOpening] = useState(null);
    const [isSliderOpen, setIsSliderOpen] = useState(false);
    const [isFullPageSliderOpen, setIsFullPageSliderOpen] = useState(openQuizImmediately);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [hasShownSlider, setHasShownSlider] = useState(false);

    const formattedPlacesCount = placesCount ? FormatAmount(placesCount) : "";
    const possessiveFormArea = area?.toLowerCase().endsWith("s")
        ? `${area}'`
        : `${area}'s`;

    const openSliderCallback = useCallback(() => {
        if (!isSliderOpen && !isFullPageSliderOpen && isMobile) {
            setIsSliderOpen(true);
        }
    }, [isSliderOpen, isFullPageSliderOpen, isMobile]);

    const openDrawer = useCallback(() => {
        if (!hasShownSlider) {
            openSliderCallback();
            trackSliderShown("Time Delay");
            setReasonForOpening("Time Delay");
            setHasShownSlider(true);
        }
    }, [openSliderCallback, hasShownSlider]);

    const scrollRef = useRef(null);
    const [mounted, setMounted] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);

    useEffect(() => {
        if (!hasShownSlider) {
            const timer = setTimeout(() => {
                openDrawer();
            }, 15 * 1000);
            return () => clearTimeout(timer);
        }
    }, [openDrawer, hasShownSlider]);

    useEffect(() => {
        if (isSliderOpen) {
            setHasShownSlider(true);
            setMounted(true);
        } else {
            const timer = setTimeout(() => {
                setMounted(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isSliderOpen, isMobile]);

    useEffect(() => {
        if (isSliderOpen && !scrollRef.current && isNewFlow) {
            scrollRef.current = true;
        }
    }, [isSliderOpen, isNewFlow]);

    useEffect(() => {
        if (isNewFlow) {
            const timer = setTimeout(openDrawer, 15 * 1000);
            return () => clearTimeout(timer);
        }
    }, [openDrawer, isNewFlow]);

    useEffect(() => {
        if (immediateFlow) {
            openDrawer();
        }
    }, [immediateFlow, openDrawer]);

    useEffect(() => {
        const modalParam = searchParams.get("modal");
        if (modalParam === "generic" || modalParam === "community") {
            setIsSliderOpen(true);
            trackSliderShown("Place Interaction");
            setReasonForOpening("Place Interaction");
            const urlSearchParams = new URLSearchParams(window.location.search);
            urlSearchParams.delete("modal");
            window.history.replaceState(
                {},
                "",
                `${window.location.pathname}?${urlSearchParams}`
            );
        }
    }, [searchParams]);

    const handleAnswerSelection = async ({
        questionText,
        answerPosition,
        answerText,
        fullAnswer,
    }) => {
        setSelectedAnswer(fullAnswer);

        trackSliderAnswerSelected({
            trigger: reasonForOpening,
            answerPosition,
            answerText,
            questionText,
        });

        setIsSliderOpen(false);

        setTimeout(() => {
            setIsFullPageSliderOpen(true);
        }, 500);
    };

    const handleDrawerButtonClick = useCallback(() => {
        if (isMobile) {
            setIsSliderOpen(true);
            setHasShownSlider(true);
        }
    }, [isMobile]);

    useEffect(() => {
        const sliderParam = searchParams.get("slider");
        if (sliderParam) {
            setIsSliderOpen(true);
            setHasShownSlider(true);
            const newSearchParams = new URLSearchParams(window.location.search);
            newSearchParams.delete("slider");
            window.history.replaceState(
                {},
                "",
                `${window.location.pathname}?${newSearchParams}`
            );
        }
    }, [searchParams]);

    const [DrawerButtonComponent, setDrawerButtonComponent] = useState(null);
    const [QuizSliderComponent, setQuizSliderComponent] = useState(null);

    const currentFlow = flowOverride
        ? flows[flowOverride]
        : flows[aiFlowVariation];

    useEffect(() => {
        getDrawerButton(currentFlow.components.drawerButton)
            .then(component => {
                setDrawerButtonComponent(() => component);
            })
            .catch(error => {
                console.error("Error loading drawer button:", error);
            });
    }, [currentFlow.components.drawerButton]);

    useEffect(() => {
        getQuizSlider(currentFlow.components.quizSlider)
            .then(component => {
                setQuizSliderComponent(() => component);
            })
            .catch(error => {
                console.error("Error loading quiz slider:", error);
            });
    }, [currentFlow.components.quizSlider]);

    console.log({ isNewFlow, QuizSliderComponent, currentFlow, isSliderOpen });

    return (
        <>
            {isNewFlow && QuizSliderComponent && (
                <QuizSliderComponent
                    immediateFlow={immediateFlow}
                    variant="drawer"
                    isOpen={isSliderOpen}
                    mounted={mounted}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    flowVersion={aiFlowVariation}
                    flowPages={currentFlow.pages}
                    onNext={(data) => {
                        handleAnswerSelection(data);
                    }}
                    onClose={() => {
                        setIsSliderOpen(false);
                        trackSliderDismissed(reasonForOpening);
                        setReasonForOpening(null);
                    }}
                />
            )}
            {isNewFlow && (
                <FullScreenQuizFlow
                    flowVersion={aiFlowVariation}
                    noCloseButton={openQuizImmediately}
                    flowPages={currentFlow.pages}
                    isFullPageSliderOpen={isFullPageSliderOpen}
                    setIsFullPageSliderOpen={setIsFullPageSliderOpen}
                    initialAnswer={selectedAnswer}
                    trialVariation={trialVariation}
                    formSubmit={formSubmit}
                    trackAction={trackAction}
                    trackView={trackView}
                    stripeData={stripeData}
                    userId={userId}
                    withTrial={withTrial}
                    branchLink={branchLink}
                    locationData={{
                        neighborhood,
                        city,
                        region,
                        country
                    }}
                />
            )}
            <FooterDrawerUI
                isVisible={isVisible}
                isMobile={isMobile}
                isSliderVariant={isNewFlow}
                area={area}
                placesCount={placesCount}
                formattedPlacesCount={formattedPlacesCount}
                possessiveFormArea={possessiveFormArea}
                setIsSliderOpen={setIsSliderOpen}
                setReasonForOpening={setReasonForOpening}
                trackSliderShown={trackSliderShown}
                trackItemTapped={trackItemTapped}
                onDrawerButtonClick={handleDrawerButtonClick}
                DrawerButtonComponent={DrawerButtonComponent}
            />
        </>
    );
};
